import dataHelpers from '@/helpers/dataHelpers'
import { formatCardKey } from '@/ai/utils/helpers'

export const createSystemMessage = (content) => {
  return { role: 'system', content }
}

export const createUserMessage = (content) => {
  return { role: 'user', content }
}

export const createAssistantMessage = (content) => {
  return { role: 'assistant', content }
}

export const debugLogMessages = (messages) => {
  if (!messages || messages.length === 0) {
    console.log('No messages to debug.')
    return
  }
  console.log('--- CHAT LOG DEBUG ---')
  for (const msg of messages) {
    console.log(`> Role: ${msg.role}`)
    console.log(`Content:\n${msg.content}`)
    console.log('----------------------')
  }
}

export const diagramToJson = (eventId, workflow, svgGraph, svgLanes, options) => {
  const excludeCards = options?.excludeCards || false
  let currentStep
  const res = []
  let index = 1

  if (!workflow || !workflow.domainEvents || workflow.domainEvents.length === 0) {
    return { workflow: [], currentStep: undefined }
  }
  if (!svgGraph || Object.keys(svgGraph).length === 0 || !svgLanes || Object.keys(svgLanes).length === 0) {
    return { workflow: [], currentStep: undefined }
  }

  const parentChildEventMap = dataHelpers.createMainParentChildEventMap(workflow.domainEvents)

  const eventArray = []
  const effectiveStartEventId = eventId || workflow.domainEvents[0]?.id

  if (!effectiveStartEventId || !svgGraph[effectiveStartEventId]) {
    // Start event ID effectiveStartEventId not found in svgGraph
    return { workflow: [], currentStep: undefined }
  }

  let tmpEventId = effectiveStartEventId
  const MAX_PARENTS = workflow.domainEvents.length * 2
  let parentCount = 0
  while (svgGraph[tmpEventId]?.parents?.[0] && svgGraph[tmpEventId].parents[0] !== 'start' && parentCount < MAX_PARENTS) {
    eventArray.unshift(tmpEventId) // Add current before moving to parent
    const parentId = svgGraph[tmpEventId].parents[0]
    if (!svgGraph[parentId]) { // Parent not in graph, stop
      // We've added tmpEventId already, which is the furthest we could go up.
      break
    }
    tmpEventId = parentId
    parentCount++
    if (eventArray.includes(tmpEventId)) { // Cycle detection
      eventArray.shift() // Remove the duplicate to break the cycle from this unshift logic
      break
    }
  }
  if (parentCount < MAX_PARENTS && svgGraph[tmpEventId] && !eventArray.includes(tmpEventId)) {
    eventArray.unshift(tmpEventId)
  }

  const currentPathNodeId = eventArray.length > 0 ? eventArray[eventArray.length - 1] : effectiveStartEventId
  if (!eventArray.includes(currentPathNodeId) && svgGraph[currentPathNodeId]) {
    if (!eventArray.includes(currentPathNodeId)) eventArray.push(currentPathNodeId)
  }

  const MAX_CHILDREN = workflow.domainEvents.length * 2
  let childrenCount = 0

  tmpEventId = eventArray.length > 0 ? eventArray[eventArray.length - 1] : effectiveStartEventId
  if (!svgGraph[tmpEventId]) {
    // Cannot determine valid node to start downward traversal
    return { workflow: [], currentStep }
  }

  while (childrenCount < MAX_CHILDREN) {
    let nextEventId = null
    if (parentChildEventMap[tmpEventId] && parentChildEventMap[tmpEventId][0]?.id) {
      nextEventId = parentChildEventMap[tmpEventId][0].id
    } else {
      const nextEventIndex = workflow.domainEvents.findIndex((ev) => ev.parents && ev.parents.includes(tmpEventId))
      if (nextEventIndex !== -1) {
        nextEventId = workflow.domainEvents[nextEventIndex].id
      }
    }

    if (nextEventId && svgGraph[nextEventId]) {
      if (eventArray.includes(nextEventId)) {
        // Cycle detected while traversing children at nextEventId. Breaking.`)
        break
      }
      eventArray.push(nextEventId)
      tmpEventId = nextEventId
    } else {
      break
    }
    childrenCount++
  }

  let previousEvent = null
  const processedEventIds = new Set()

  for (const eventIdInPath of eventArray) {
    if (processedEventIds.has(eventIdInPath) || !svgGraph[eventIdInPath]) {
      continue
    }
    processedEventIds.add(eventIdInPath)

    const event = svgGraph[eventIdInPath]
    const cards = {}
    if (!excludeCards && event.cards) {
      for (const card of event.cards) {
        if (!card.cardType || !card.cardType.title) {
          continue
        }
        const key = formatCardKey(card.cardType.title)
        if (!cards[key]) { cards[key] = [] }
        cards[key].push(card.description)
      }
    }

    const roleName = svgLanes[event.laneId]?.name || 'Unknown Role'

    const stepData = {
      step: index,
      role: roleName,
      description: event.name || 'Unnamed Event',
      ...cards
    }

    if (previousEvent?.type === 'bpmn:ExclusiveGateway') {
      const conditionIndex = event.conditionLabels?.findIndex(label => label.parentId === previousEvent.id)
      if (conditionIndex > -1 && res.length > 0) {
        res[res.length - 1].description += ` : ${event.conditionLabels[conditionIndex].name}`
      }
    }

    res.push(stepData)

    if (event.id === effectiveStartEventId) {
      currentStep = index
    }
    previousEvent = event
    index++
  }

  return { workflow: res, currentStep }
}

export const getCardTypeMetadata = (cardTypes) => {
  const content = []

  if (!cardTypes || !Array.isArray(cardTypes)) {
    return ''
  }

  cardTypes.forEach((cardType) => {
    if (cardType.generateWithAI) {
      content.push(`${formatCardKey(cardType.title)} : ${cardType.description}`)
    }
  })

  return content
}

export const getWorkflowBackgroundSections = (workflow, currentDiagram, cardTypes) => {
  const promptSections = []

  promptSections.push({
    title: 'Background - Workflow Description (business process, workflow or project scope)',
    content: workflow.aiDescription || 'N/A'
  })

  promptSections.push({
    title: 'Background workflow (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(currentDiagram)
  })

  promptSections.push({
    title: 'Clarification',
    content: [
      `Each step in the workflow JSON contains the step number, the role and the description.
       Each step (in the workflow) can also include metadata with the following meanings:`,
      ...getCardTypeMetadata(cardTypes)
    ]
  })

  return promptSections
}

export const getRoleCommandAggregateBackground = ({ currentStep, roleName, description, commandName, aggregateName }) => {
  let content = ''

  content += `The domain event in the workflow that we are working with right now is the event: ${currentStep}: ${roleName} - ${description}. `
  if (commandName && aggregateName) {
    content += `This step involves the role (or actor) "${roleName}" invoking the command "${commandName}" on the aggregate "${aggregateName}".`
  } else {
    content += `This step involves the role (or actor) "${roleName}" invoking a command on an aggregate (create, update or delete).`
  }
  return content
}
